import { getAppConfigSingleton } from "@expert/app-config";
import type { Channel } from "@expert/shared-types";
import { createContext, useContext, useState } from "react";
import { type ExpertUniversityConfigurationContextType, EnabledPartners as enabledPartners } from "../types";
import { partnerEnabledForExpertUniversity } from "../utils/partner";

export const ExpertUniversityConfigurationContext = createContext<ExpertUniversityConfigurationContextType | undefined>(
    undefined,
);

interface ExpertUniversityProviderProps {
    children?: React.ReactNode;
    userCache: Record<string, any>;
    channel: Channel;
}

export function ExpertUniversityConfigurationProvider({
    children,
    userCache,
    channel,
}: React.PropsWithChildren<ExpertUniversityProviderProps>) {
    const sandboxProfileId = "paula_smith";
    const PROFILE_TESTING_GROUP = "HQDOMAIN_APP-NON-VZW-APP-SOLUTO-TRAINING-ASSIST-";
    const { allowedPartners: appConfigAllowedPartners } = getAppConfigSingleton();
    //Display all enabled partners if user is in testing group
    const shouldDisplayAllEnabledPartners = userCache.groups.includes(PROFILE_TESTING_GROUP);
    const [taskInSandbox, setTaskInSandbox] = useState<boolean>(false);
    const [trainingPartner, setTrainingPartner] = useState<string>(
        enabledPartners.find((partner) => partnerEnabledForExpertUniversity(partner, channel)) ||
            appConfigAllowedPartners[0],
    );

    return (
        <ExpertUniversityConfigurationContext.Provider
            value={{
                allowedPartners: shouldDisplayAllEnabledPartners ? [...enabledPartners] : appConfigAllowedPartners,
                taskInSandbox,
                sandboxProfileId,
                setTaskInSandbox,
                setTrainingPartner,
                trainingPartner,
            }}
        >
            {children}
        </ExpertUniversityConfigurationContext.Provider>
    );
}

export function useExpertUniversityConfiguration() {
    const context = useContext(ExpertUniversityConfigurationContext);
    if (!context) {
        throw new Error(
            "useExpertUniversityConfiguration must be used within an ExpertUniversityConfigurationProvider",
        );
    }
    return context;
}
